import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import {
  localFetchClientById,
  localFetchClientsPaginated,
} from '@/renderer/lib/powersync/localOrderOps';
import { updateCompanyClient } from '@/renderer/services/orderService';
import { UpdateSalesClientInput } from '@/renderer/types/order';
import { apiError, apiSuccess } from '@/renderer/util/notify';

interface UseClientsPaginatedParams {
  search?: string;
  page: number;
  pageSize: number;
}

/**
 * Paginated client list, read from local PowerSync SQLite so the Clients
 * page works offline like the rest of the app. Reactively invalidated
 * whenever local `clients` rows change (own edits, or synced in remotely).
 */
export function useClientsPaginated(params: UseClientsPaginatedParams) {
  const queryClient = useQueryClient();

  usePowerSyncChange(['clients'], () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
  });

  return useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: async () => {
      const res = await localFetchClientsPaginated(params);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to fetch clients');
      }
      return res.data;
    },
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch a single client by id, with the full editable field set. Used to
 * seed the shared ClientEditorModal from contexts (like the Guests page)
 * that only hold a partial client-derived view, not the full clients row.
 */
export function useClientById(clientId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.clients.byId(clientId),
    queryFn: async () => {
      const res = await localFetchClientById(clientId as string);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to fetch client');
      }
      return res.data;
    },
    enabled: Boolean(clientId),
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateSalesClientInput) => {
      const res = await updateCompanyClient(payload);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to update client');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      apiSuccess('Client updated successfully');
    },
    onError: (err: Error) => apiError(err.message),
  });
}
